export function excelSerialToDate(serial: number, use1904 = false): Date {
  const epochOffsetDays = use1904 ? 24107 : 25569; // 1904 vs 1900 date system
  const ms = Math.round((serial - epochOffsetDays) * 86400 * 1000);
  return new Date(ms);
}

export function isNumericString(s: string): boolean {
  return /^[0-9]+(\.[0-9]+)?$/.test(s.trim());
}

/** Parse date strings like dd/mm/yy, dd-mm-yyyy, mm/dd/yyyy, yyyy-mm-dd, etc. */
export function parseFlexibleDateString(s: string): Date | null {
  const v = s.trim();

  // Try native for ISO-like (YYYY-MM-DD, RFC3339) first.
  // (Avoids ambiguous "11-11-2025" which native Date may not parse consistently.)
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}(?:[ T].*)?$/.test(v)) {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d;
  }

  // Generic D/M/Y or M/D/Y with -, /, . separators; 2- or 4-digit year
  const m = /^(\d{1,4})[./-](\d{1,2})[./-](\d{1,4})$/.exec(v);
  if (!m) return null;

  const [, aStr, bStr, cStr] = m;
  const A = Number(aStr),
    B = Number(bStr),
    C = Number(cStr);

  let year: number, month: number, day: number;

  if (aStr.length === 4) {
    // Y-M-D
    year = A;
    month = B;
    day = C;
  } else if (cStr.length === 4) {
    // Prefer D-M-Y as per your examples; fallback to M-D-Y when obvious
    year = C;
    if (A > 12) {
      day = A;
      month = B;
    } else if (B > 12) {
      day = B;
      month = A;
    } else {
      day = A;
      month = B;
    } // prefer D-M-Y when ambiguous
  } else {
    // 2-digit year -> windowing (69–99 => 1900s, 00–68 => 2000s)
    const yy = C;
    year = yy >= 69 ? 1900 + yy : 2000 + yy;
    if (A > 12) {
      day = A;
      month = B;
    } else if (B > 12) {
      day = B;
      month = A;
    } else {
      day = A;
      month = B;
    } // prefer D-M-Y when ambiguous
  }

  // Basic range checks + overflow validation
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Use UTC midnight to avoid TZ shifts
  const dt = new Date(Date.UTC(year, month - 1, day));
  return dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
    ? dt
    : null;
}

export function parseShipDate(input: unknown, use1904 = false): Date | null {
  if (input == null) return null;

  if (typeof input === "number" && Number.isFinite(input)) {
    return excelSerialToDate(input, use1904);
  }

  if (typeof input === "string") {
    const trimmed = input.trim();
    if (isNumericString(trimmed)) {
      return excelSerialToDate(Number(trimmed), use1904);
    }
    const parsed = parseFlexibleDateString(trimmed);
    if (parsed) return parsed;

    // last resort (handles things like "Aug 14, 2025")
    const native = new Date(trimmed);
    return isNaN(native.getTime()) ? null : native;
  }

  if (input instanceof Date && !isNaN(input.getTime())) {
    return new Date(input.getTime());
  }

  return null;
}
